import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, LockIcon } from 'lucide-react';
import { PortalHeader } from '../components/shell/PortalHeader';
import { LoanOfficerChip } from '../components/shell/LoanOfficerChip';
import { SectionRail } from '../components/application/SectionRail';
import { MobileProgress } from '../components/application/MobileProgress';
import { stepRegistry } from '../components/application/stepRegistry';
import { useReviewIssues } from '../components/application/steps/ReviewStep';
import { SaveIndicator } from '../components/ui/SaveIndicator';
import { Button } from '../components/ui/Button';
import { usePortal } from '../contexts/PortalContext';
import { groupForStep, minutesLeft, neighbors, visibleGroups } from '../utils/applicationFlow';
const ease = [0.23, 1, 0.32, 1] as const;
export function Application() {
  const {
    stepId = 'goal'
  } = useParams();
  const [params] = useSearchParams();
  const returnTo = params.get('from');
  const fromReview = returnTo === 'review' || returnTo === 'credit';
  const navigate = useNavigate();
  const {
    answers,
    completed,
    markComplete,
    setLastStep
  } = usePortal();
  const issues = useReviewIssues();
  const [direction, setDirection] = useState(1);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number>();
  const entry = stepRegistry[stepId];
  const groups = visibleGroups(answers);
  const {
    prev,
    next
  } = neighbors(stepId, answers);
  const isReview = stepId === 'review';
  const canContinue = isReview ? issues === 0 : entry?.canContinue ? entry.canContinue(answers) : true;
  useEffect(() => {
    setLastStep(stepId);
    window.scrollTo({
      top: 0
    });
  }, [stepId, setLastStep]);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);
  if (!entry) return <Navigate to="/apply/goal" replace data-id="element-727" data-mp="2lu776rycehsy" />;
  const goNext = () => {
    if (isReview) {
      navigate('/credit');
      return;
    }
    markComplete(stepId);
    if (returnTo === 'credit') {
      navigate('/credit');
      return;
    }
    const target = fromReview ? 'review' : next;
    if (!target) return;
    const currentGroup = groupForStep(stepId, answers);
    const nextGroup = groupForStep(target, answers);
    if (currentGroup && nextGroup && currentGroup.id !== nextGroup.id && !fromReview) {
      setToast(`${currentGroup.label} complete`);
      window.clearTimeout(toastTimer.current);
      toastTimer.current = window.setTimeout(() => setToast(''), 1800);
    }
    setDirection(1);
    navigate(`/apply/${target}`);
  };
  const goBack = () => {
    setDirection(-1);
    if (prev) navigate(`/apply/${prev}`);else navigate('/');
  };
  const continueLabel = isReview ? issues ? `Fix ${issues} item${issues > 1 ? 's' : ''} to continue` : 'Continue to credit' : returnTo === 'credit' ? 'Save and return to credit' : fromReview ? 'Save and return to review' : 'Continue';
  return <div className="min-h-screen w-full bg-canvas" data-id="element-728" data-mp="3bziafbollz0l">
      <PortalHeader maxWidth="max-w-7xl" right={<>
            <span className="hidden md:block" data-id="element-730" data-mp="3gtw2b441xo0i">
              <SaveIndicator data-id="element-731" data-mp="3lpl47e3jngzz" />
            </span>
            <LoanOfficerChip data-id="element-732" data-mp="3lsfn316wstra" />
          </>} data-id="element-729" data-mp="3grrz6odz8t1a" />

      <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-40 pt-5 md:px-8 lg:grid-cols-[260px_1fr] lg:pb-24 lg:pt-12 xl:grid-cols-[280px_1fr_200px]" data-id="element-733" data-mp="2o45u1bp3pgcb">
        <aside className="hidden lg:block" data-id="element-734" data-mp="2y1a8sqgma9j3">
          <div className="sticky top-28" data-id="element-735" data-mp="2y08zaqf91v3h">
            <SectionRail groups={groups} activeStep={stepId} completed={completed} data-id="element-736" data-mp="322nft3vg4irl" />
            <p className="mt-8 px-3 text-[13px] leading-relaxed text-ink-400" data-id="element-737" data-mp="320j2jvbnq7en">About {minutesLeft(completed, answers)} minutes left. Your progress is saved automatically.</p>
          </div>
        </aside>

        <main className="min-w-0" data-id="element-738" data-mp="he95wtzxcarl">
          <MobileProgress groups={groups} activeStep={stepId} completed={completed} data-id="element-739" data-mp="fuppkoewc5sx" />

          <div className="mx-auto mt-8 max-w-[600px] lg:mt-0" data-id="element-740" data-mp="awede714pj7j">
            <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-741" data-mp="ay8zhoo4zhd7">
              <motion.div key={stepId} custom={direction} initial={{
              opacity: 0,
              x: direction * 24
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -16
            }} transition={{
              duration: 0.22,
              ease
            }} data-id="element-742" data-mp="pv0184la955z">
                {entry.render()}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 hidden items-center justify-between lg:flex" data-id="element-743" data-mp="18j4x6pvp7lu8">
              <Button variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-745" data-mp="13mwmppsg4gte" />} onClick={goBack} data-id="element-744" data-mp="13m4w6tdph99z">
                Back
              </Button>
              <Button size="lg" variant={isReview && issues ? 'secondary' : 'primary'} disabled={!canContinue} onClick={goNext} trailingIcon={canContinue ? <ArrowRightIcon className="h-5 w-5" data-id="element-747" data-mp="1w8cz5aptq9mc" /> : undefined} data-id="element-746" data-mp="1dizrz4ilgvkk">
                {continueLabel}
              </Button>
            </div>
            {isReview && <p className="mt-6 flex items-center gap-2 text-[13px] text-ink-400" data-id="element-748" data-mp="1n6vjftgbw9y6">
                <LockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden data-id="element-749" data-mp="1s5onkv9q6olo" /> Next, you’ll authorize a credit check and choose a loan option. You’ll submit your application after that.
              </p>}
          </div>
        </main>
      </div>

      {/* Mobile action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-200 bg-white/95 px-4 pb-6 pt-3 backdrop-blur lg:hidden" data-id="element-750" data-mp="1i9lojya6762y">
        <div className="flex gap-2" data-id="element-751" data-mp="18hfumj0cyel4">
          <Button variant="secondary" size="lg" className="w-14 px-0" onClick={goBack} aria-label="Back" data-id="element-752" data-mp="18iiomzsz8x59">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-753" data-mp="uig9hlc20htp" />
          </Button>
          <Button size="lg" className="flex-1" disabled={!canContinue} onClick={goNext} data-id="element-754" data-mp="zhap6eeq5q9l">
            {continueLabel}
          </Button>
        </div>
        <div className="mt-2 flex justify-center" data-id="element-755" data-mp="318b6gmk5sqfi">
          <SaveIndicator data-id="element-756" data-mp="2wf8903c9gwu1" />
        </div>
      </div>

      <div className="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center" data-id="element-757" data-mp="2wddp8scq7arj">
        <AnimatePresence data-id="element-758" data-mp="2sbsfhfvhnclk">
          {toast && <motion.div role="status" initial={{
          opacity: 0,
          y: -12,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.22,
          ease
        }} className="flex items-center gap-2 rounded-full bg-ink-900 py-2 pl-2 pr-4 text-sm font-medium text-white shadow-lift" data-id="element-759" data-mp="2nfl7zing1t1i">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-positive-500" data-id="element-760" data-mp="3648c9yxsqee1">
                <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden data-id="element-761" data-mp="1zphe430257np" />
              </span>
              {toast}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}